import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Pencil, Trash2, Repeat } from 'lucide-react';
import { BudgetItem } from '@/hooks/use-annual-budget';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface BudgetTableProps {
  items: BudgetItem[];
  getActualAmount: (categoryId: string | null, month: number, recurrenceId?: string | null) => number;
  onEditItem: (item: BudgetItem) => void;
  onDeleteItem: (id: string) => void;
  currentMonth: number;
}

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

interface GroupedItems {
  [categoryName: string]: {
    categoryId: string | null;
    color: string;
    icon: string;
    items: BudgetItem[];
  };
}

export function BudgetTable({
  items,
  getActualAmount,
  onEditItem,
  onDeleteItem,
  currentMonth,
}: BudgetTableProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['Sem Categoria']));

  const formatCurrency = (value: number) => {
    if (value === 0) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const groupedItems = useMemo(() => {
    const groups: GroupedItems = {};
    
    items.forEach(item => {
      const categoryName = item.category?.name || 'Sem Categoria';
      if (!groups[categoryName]) {
        groups[categoryName] = {
          categoryId: item.category_id,
          color: item.category?.color || '#6366f1',
          icon: item.category?.icon || 'CircleDot',
          items: [],
        };
      }
      groups[categoryName].items.push(item);
    });

    return groups;
  }, [items]);

  const getPlannedValue = (item: BudgetItem, month: number): number => {
    if (!item.active_months.includes(month)) return 0;
    
    const monthValue = item.month_values?.find(mv => mv.month === month);
    if (monthValue) return monthValue.planned_amount;
    
    if (item.frequency === 'monthly') return item.monthly_amount;
    if (item.frequency === 'annual' && item.active_months[0] === month) return item.monthly_amount;
    
    return 0;
  };

  const getItemAnnualTotal = (item: BudgetItem): number => {
    return MONTHS.reduce((sum, _, i) => sum + getPlannedValue(item, i + 1), 0);
  };

  const getCategoryMonthTotal = (categoryItems: BudgetItem[], month: number): number => {
    return categoryItems.reduce((sum, item) => sum + getPlannedValue(item, month), 0);
  };

  const getCategoryAnnualTotal = (categoryItems: BudgetItem[]): number => {
    return categoryItems.reduce((sum, item) => sum + getItemAnnualTotal(item), 0);
  };

  const getCellStatus = (planned: number, actual: number, month: number): string => {
    if (month > currentMonth) return 'bg-muted/30';
    if (planned === 0) return '';
    const ratio = actual / planned;
    if (ratio <= 0.8) return 'bg-emerald-500/20';
    if (ratio <= 1) return 'bg-amber-500/20';
    return 'bg-red-500/20';
  };

  const toggleCategory = (name: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(name)) {
      newExpanded.delete(name);
    } else {
      newExpanded.add(name);
    }
    setExpandedCategories(newExpanded);
  };

  const grandTotal = items.reduce((sum, item) => sum + getItemAnnualTotal(item), 0);

  return (
    <>
      {/* Mobile Card Layout */}
      <div className="md:hidden space-y-3">
        {Object.entries(groupedItems).map(([categoryName, group]) => {
          const isExpanded = expandedCategories.has(categoryName);
          const categoryTotal = getCategoryAnnualTotal(group.items);

          return (
            <div key={categoryName} className="border rounded-lg overflow-hidden">
              {/* Category header - clickable to expand/collapse */}
              <button
                className="w-full flex items-center justify-between p-4 min-h-[48px] hover:bg-muted/50 text-left"
                onClick={() => toggleCategory(categoryName)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: group.color }}
                  />
                  <span className="font-medium text-sm truncate">{categoryName}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-bold text-sm">{formatCurrency(categoryTotal)}</span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </div>
              </button>

              {/* Expanded items */}
              {isExpanded &&
                group.items.map(item => {
                  const itemTotal = getItemAnnualTotal(item);

                  return (
                    <div key={item.id} className="border-t p-4 space-y-3">
                      {/* Item name + badges + actions */}
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
                          <span className="text-sm font-medium truncate">{item.name}</span>
                          {item.is_fixed && (
                            <span className="text-xs bg-blue-500/20 text-blue-500 px-1.5 py-0.5 rounded">
                              Fixo
                            </span>
                          )}
                          {item.provision_enabled && (
                            <span className="text-xs bg-purple-500/20 text-purple-500 px-1.5 py-0.5 rounded">
                              Provisao
                            </span>
                          )}
                          {item.recurrence_id && (
                            <span className="text-xs bg-emerald-500/20 text-emerald-500 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                              <Repeat className="h-3 w-3" />
                              Rec.
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10"
                            onClick={() => onEditItem(item)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 text-destructive"
                            onClick={() => onDeleteItem(item.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* 12 months in 3-column grid */}
                      <div className="grid grid-cols-3 gap-2">
                        {MONTHS.map((month, i) => {
                          const monthNum = i + 1;
                          const planned = getPlannedValue(item, monthNum);
                          const actual = getActualAmount(item.category_id, monthNum, item.recurrence_id);
                          return (
                            <div
                              key={i}
                              className={cn(
                                'text-center p-2 rounded',
                                getCellStatus(planned, actual, monthNum),
                                monthNum === currentMonth
                                  ? 'ring-2 ring-primary/30'
                                  : 'bg-muted/30'
                              )}
                            >
                              <div className="text-[10px] text-muted-foreground uppercase">
                                {month}
                              </div>
                              <div className="text-xs font-medium">
                                {formatCurrency(planned)}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Item total */}
                      <div className="flex justify-between text-sm pt-1 border-t">
                        <span className="text-muted-foreground">Total</span>
                        <span className="font-bold">{formatCurrency(itemTotal)}</span>
                      </div>
                    </div>
                  );
                })}
            </div>
          );
        })}

        {/* Mobile Grand Total */}
        <div className="border rounded-lg bg-primary/10 p-4">
          <div className="flex justify-between items-center">
            <span className="font-bold text-sm">TOTAL GERAL</span>
            <span className="font-bold text-sm">{formatCurrency(grandTotal)}</span>
          </div>
        </div>
      </div>

      {/* Desktop Table Layout */}
      <div className="hidden md:block overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 font-medium sticky left-0 bg-muted/50 min-w-[200px]">
                Item
              </th>
              <th className="text-right p-3 font-medium min-w-[100px]">Total</th>
              {MONTHS.map((month, i) => (
                <th
                  key={month}
                  className={cn(
                    'text-right p-3 font-medium min-w-[80px]',
                    i + 1 === currentMonth && 'bg-primary/10'
                  )}
                >
                  {month}
                </th>
              ))}
              <th className="text-center p-3 font-medium w-[80px]">Acoes</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedItems).map(([categoryName, group]) => (
              <>
                {/* Category Header Row */}
                <tr
                  key={`cat-${categoryName}`}
                  className="bg-muted/30 cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleCategory(categoryName)}
                >
                  <td className="p-3 font-medium sticky left-0 bg-muted/30">
                    <div className="flex items-center gap-2">
                      {expandedCategories.has(categoryName) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: group.color }}
                      />
                      {categoryName}
                    </div>
                  </td>
                  <td className="text-right p-3 font-medium">
                    {formatCurrency(getCategoryAnnualTotal(group.items))}
                  </td>
                  {MONTHS.map((_, i) => (
                    <td
                      key={i}
                      className={cn(
                        'text-right p-3 font-medium',
                        i + 1 === currentMonth && 'bg-primary/10'
                      )}
                    >
                      {formatCurrency(getCategoryMonthTotal(group.items, i + 1))}
                    </td>
                  ))}
                  <td className="p-3" />
                </tr>

                {/* Item Rows */}
                {expandedCategories.has(categoryName) &&
                  group.items.map(item => (
                    <tr key={item.id} className="border-t hover:bg-muted/20">
                      <td className="p-3 pl-10 sticky left-0 bg-background">
                        <div className="flex items-center gap-2">
                          <span className="truncate">{item.name}</span>
                          {item.is_fixed && (
                            <span className="text-xs bg-blue-500/20 text-blue-500 px-1.5 py-0.5 rounded">
                              Fixo
                            </span>
                          )}
                          {item.provision_enabled && (
                            <span className="text-xs bg-purple-500/20 text-purple-500 px-1.5 py-0.5 rounded">
                              Provisao
                            </span>
                          )}
                          {item.recurrence_id && (
                            <span className="text-xs bg-emerald-500/20 text-emerald-500 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                              <Repeat className="h-3 w-3" />
                              Recorrente
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-right p-3 font-medium">
                        {formatCurrency(getItemAnnualTotal(item))}
                      </td>
                      {MONTHS.map((_, i) => {
                        const month = i + 1;
                        const planned = getPlannedValue(item, month);
                        const actual = getActualAmount(item.category_id, month, item.recurrence_id);
                        return (
                          <td
                            key={i}
                            className={cn(
                              'text-right p-3',
                              getCellStatus(planned, actual, month),
                              i + 1 === currentMonth && 'ring-2 ring-primary/30'
                            )}
                            title={`Planejado: ${formatCurrency(planned)}\nReal: ${formatCurrency(actual)}`}
                          >
                            {formatCurrency(planned)}
                          </td>
                        );
                      })}
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditItem(item);
                            }}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteItem(item.id);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </>
            ))}

            {/* Grand Total Row */}
            <tr className="bg-primary/10 font-bold border-t-2">
              <td className="p-3 sticky left-0 bg-primary/10">TOTAL GERAL</td>
              <td className="text-right p-3">{formatCurrency(grandTotal)}</td>
              {MONTHS.map((_, i) => {
                const monthTotal = items.reduce(
                  (sum, item) => sum + getPlannedValue(item, i + 1),
                  0
                );
                return (
                  <td
                    key={i}
                    className={cn(
                      'text-right p-3',
                      i + 1 === currentMonth && 'ring-2 ring-primary/50'
                    )}
                  >
                    {formatCurrency(monthTotal)}
                  </td>
                );
              })}
              <td className="p-3" />
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
